import { labelForAuditEntity } from "../auditEntityLabels";
import { formatBRL } from "../format";
import type { AuditLogRow, AuditSummary, Lookups, Ref, TargetKind } from "./index";

// ---------------------------------------------------------------------------
// Safe readers. `details` is jsonb: it may be an object, but also a string,
// array, number or null. None of these throw.
// ---------------------------------------------------------------------------

export function asRecord(details: unknown): Record<string, unknown> | null {
  if (details === null || typeof details !== "object" || Array.isArray(details)) return null;
  return details as Record<string, unknown>;
}

function read(details: unknown, key: string): unknown {
  return asRecord(details)?.[key];
}

/** A non-empty string, or null. */
export function str(details: unknown, key: string): string | null {
  const v = read(details, key);
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

/** A finite number, or null. Numeric strings are not coerced. */
export function num(details: unknown, key: string): number | null {
  const v = read(details, key);
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

export function bool(details: unknown, key: string): boolean | null {
  const v = read(details, key);
  return typeof v === "boolean" ? v : null;
}

export function obj(details: unknown, key: string): Record<string, unknown> | null {
  return asRecord(read(details, key));
}

export function arr(details: unknown, key: string): unknown[] | null {
  const v = read(details, key);
  return Array.isArray(v) ? v : null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_RE.test(value);
}

/** First 8 chars of an id, shown in mono next to a fallback text. */
function shortId(id: string): string {
  return id.slice(0, 8);
}

// ---------------------------------------------------------------------------
// Formatting (KTD11)
// ---------------------------------------------------------------------------

/**
 * "2026-11-30" (or the date part of an ISO timestamp) -> "30/11/2026".
 * Split by string on purpose: `new Date("2026-11-30")` is UTC midnight and
 * renders the day before in BRT.
 */
export function formatIsoDay(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

/** Cents -> "R$ 15,00" (with the U+00A0 formatBRL emits). */
export function formatCents(cents: number): string {
  return formatBRL(cents / 100);
}

export function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** `details[key]` (default "count") as "1 one" / "n many"; `zero` replaces a 0 when given. */
export function countText(
  details: unknown,
  opts: { key?: string; zero?: string; one: string; many: string },
): string | null {
  const n = num(details, opts.key ?? "count");
  if (n === null) return null;
  return n === 0 && opts.zero ? opts.zero : plural(n, opts.one, opts.many);
}

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const OP_LABELS: Record<string, string> = {
  insert: "criou",
  update: "alterou",
  delete: "apagou",
  sync: "sincronizou",
  reorder: "reordenou",
  upsert: "gravou",
};

/** Column names written to `details.fields`, lower-case for mid-sentence use. */
export const FIELD_LABELS: Record<string, string> = {
  name: "nome",
  icon: "ícone",
  title: "título",
  description: "descrição",
  content: "texto",
  body: "texto",
  subject: "assunto",
  note: "texto da nota",
  status: "status",
  slug: "tipo",
  version: "versão",
  sort_order: "ordem",
  is_active: "ativo",
  is_published: "publicação",
  visibility: "visibilidade",
  config: "configuração",
  limits: "limites",
  permissions: "permissões",

  tier_id: "plano",
  tier_access: "planos com acesso",
  self_service_signup: "adesão pelo app",
  monthly_price_cents: "preço mensal",
  renews: "renovação",
  ending_notice_enabled: "aviso de término",
  ending_notice_days: "aviso de término",
  billing_interval: "período de cobrança",
  billing_interval_count: "período de cobrança",
  discount_type: "tipo de desconto",
  grace_period_days: "dias de carência",
  blocked_features: "recursos bloqueados",
  banner_message: "aviso no app",
  banner_cta_label: "aviso no app",
  blocked_subject: "e-mail de bloqueio",
  blocked_body: "e-mail de bloqueio",
  days_before: "dias antes",
  channels: "canais",
  sent_at: "data de envio",
  paid_at: "data de pagamento",
  voided_at: "data de anulação",
  due_date: "vencimento",
  notes: "observações",
  payment_url: "link de pagamento",
  pdf_path: "PDF",
  payment_provider: "meio de cobrança",
  payment_provider_id: "meio de cobrança",

  full_name: "nome",
  email: "e-mail",
  phone: "telefone",
  company: "empresa",
  job_title: "cargo",
  sector: "setor",
  bio: "bio",
  birthday: "aniversário",
  contract_start: "membro desde",

  cover_image: "capa",
  photos: "fotos",
  starts_at: "data e hora",
  ends_at: "data e hora",
  capacity: "capacidade",
  invite_only: "só convidados",
  is_paid: "pago",
  max_installments: "parcelas",
  responsible_staff_ids: "responsáveis",
  location_text: "local",
  meeting_url: "link da reunião",
  dress_code: "traje",
  seating_mode: "modo de lugares",
  access_terms: "termos de acesso",
  media_type: "tipo de mídia",
  category_id: "categoria",
  venue_id: "local",
  benefit_id: "benefício ligado",
  partner_name: "parceiro",
  stock_total: "estoque",
  address: "endereço",
  latitude: "localização no mapa",
  longitude: "localização no mapa",
  geofence_radius_m: "raio do check-in",

  asaas_members_enabled: "cobrança Asaas de membros",
  invitation_ttl_days: "validade do convite (dias)",
  allowed_payment_methods: "formas de pagamento aceitas",
  default_payment_method: "forma de pagamento padrão",
  asaas_members_api_key_ref: "credenciais Asaas",
  asaas_members_webhook_secret_ref: "credenciais Asaas",
};

const HIDDEN_FIELDS: ReadonlySet<string> = new Set(["cover_blurhash"]);

/**
 * `["name", "description", "x", "y", "z"]` -> "nome, descrição, x e mais 2".
 * Unknown names pass through; labels shared by two columns show once.
 * Returns null when nothing is left to show.
 */
export function translateFields(fields: unknown, max = 3): string | null {
  if (!Array.isArray(fields)) return null;
  const labels: string[] = [];
  for (const f of fields) {
    if (typeof f !== "string" || f === "" || HIDDEN_FIELDS.has(f)) continue;
    const label = FIELD_LABELS[f] ?? f;
    if (!labels.includes(label)) labels.push(label);
  }
  if (labels.length === 0) return null;
  const cap = Math.max(1, max);
  if (labels.length <= cap) return labels.join(", ");
  return `${labels.slice(0, cap).join(", ")} e mais ${labels.length - cap}`;
}

/**
 * `{verb} {translated details.fields}`, or null. `rename` maps column names
 * before `translateFields`, which passes the mapped text through.
 */
export function fieldsFragment(details: unknown, verb = "alterou", rename?: Record<string, string>): string | null {
  const raw = asRecord(details)?.fields;
  const fields = rename && Array.isArray(raw) ? raw.map((f) => (typeof f === "string" ? (rename[f] ?? f) : f)) : raw;
  const text = translateFields(fields);
  return text ? `${verb} ${text}` : null;
}

/** The row's entity label; `fallback` replaces an empty label. */
export function entityLabelOf(row: AuditLogRow, fallback = ""): string {
  return labelForAuditEntity(typeof row.entity_type === "string" ? row.entity_type : "") || fallback;
}

/** Enum value -> pt-BR, unknown values pass through as the raw code. */
export function translate(map: Record<string, string>, value: string): string {
  return map[value] ?? value;
}

// ---------------------------------------------------------------------------
// Resolution (KTD9)
// ---------------------------------------------------------------------------

export type ResolutionState = "ok" | "missing" | "failed" | "pending";

export interface Resolution {
  text: string;
  state: ResolutionState;
  /** The id that was looked up, when there was one. */
  id?: string;
}

export const FAILED_TEXT = "não consegui identificar";

/** Kinds with a dedicated not-found text; anything else reads "removido do sistema". */
export type MissingKind = TargetKind | "profile" | "account";

const MISSING_TEXT: Partial<Record<MissingKind, string>> = {
  profile: "usuário removido",
  account: "conta excluída",
  activity: "atividade removida",
  tier: "plano removido",
  legal_document: "documento removido",
  benefit: "benefício removido",
  feed_post: "post removido",
};

export function missingText(kind: MissingKind): string {
  return MISSING_TEXT[kind] ?? "removido do sistema";
}

export function targetKey(kind: TargetKind, id: string): string {
  return `${kind}:${id}`;
}

/** Inverse of `targetKey`. */
export function parseTargetKey(key: string): Ref {
  const at = key.indexOf(":");
  return { kind: key.slice(0, at) as TargetKind, id: key.slice(at + 1) };
}

/**
 * A person by `profiles.id` (never `profiles.user_id`, R9). Shows the email,
 * else the name. `fallbackKind` picks the not-found text.
 */
export function resolvePerson(
  lookups: Lookups,
  id: unknown,
  fallbackKind: MissingKind = "profile",
): Resolution {
  if (!isUuid(id)) return { text: missingText(fallbackKind), state: "missing" };
  if (lookups.profiles.has(id)) {
    const p = lookups.profiles.get(id);
    const text = p?.email?.trim() || p?.full_name?.trim();
    return text ? { text, state: "ok", id } : { text: missingText(fallbackKind), state: "missing", id };
  }
  if (lookups.failed.profiles.has(id)) return { text: FAILED_TEXT, state: "failed", id };
  if (lookups.pending) return { text: "", state: "pending", id };
  return { text: missingText(fallbackKind), state: "missing", id };
}

/** A target by the RPC kind. A key present with a null label is resolved-not-found. */
export function resolveTarget(lookups: Lookups, kind: TargetKind, id: unknown): Resolution {
  if (!isUuid(id)) return { text: missingText(kind), state: "missing" };
  const key = targetKey(kind, id);
  if (lookups.targets.has(key)) {
    const raw: unknown = lookups.targets.get(key);
    const label = typeof raw === "string" ? raw.trim() : "";
    return label ? { text: label, state: "ok", id } : { text: missingText(kind), state: "missing", id };
  }
  if (lookups.failed.targets.has(key)) return { text: FAILED_TEXT, state: "failed", id };
  if (lookups.pending) return { text: "", state: "pending", id };
  return { text: missingText(kind), state: "missing", id };
}

/**
 * A secondary fragment that depends on a lookup. Pending and failed lookups
 * drop the fragment (KTD9), so the line does not shift when it arrives and a
 * failed read never shows as "removido". Not found shows the kind fallback.
 */
export function lookupFragment(res: Resolution, format: (text: string) => string = (t) => t): string | null {
  if (res.state === "ok") return format(res.text);
  if (res.state === "missing") return res.text;
  return null;
}

/**
 * A lookup fragment for a recorded id; an id never recorded adds nothing,
 * rather than claiming the record was removed. `onMissing` overrides the
 * not-found text.
 */
export function refFragment(
  res: Resolution,
  format: (text: string) => string,
  onMissing?: (text: string) => string,
): string | null {
  if (!res.id) return null;
  if (res.state === "missing" && onMissing) return onMissing(res.text);
  return lookupFragment(res, format);
}

export type Fragment = string | null | undefined | false;

/** Builds a summary with a consistent primary/targetState/rawId from a resolution or plain text. */
export function buildSummary(primary: Resolution | string, secondary: readonly Fragment[] = []): AuditSummary {
  const fragments = secondary.filter((f): f is string => typeof f === "string" && f.trim() !== "");
  if (typeof primary === "string") return { primary, secondary: fragments, targetState: "ok" };
  const rawId = primary.id ? shortId(primary.id) : undefined;
  switch (primary.state) {
    case "ok":
      return { primary: primary.text, secondary: fragments, targetState: "ok" };
    case "pending":
      return { primary: { pending: true }, secondary: fragments, targetState: "ok" };
    case "failed":
      return { primary: FAILED_TEXT, secondary: fragments, targetState: "failed", ...(rawId ? { rawId } : {}) };
    case "missing":
      return { primary: primary.text, secondary: fragments, targetState: "missing", ...(rawId ? { rawId } : {}) };
  }
}
